/**
 * 1.3.75: MVP 多租户架构 - 企业信息补充页面
 * 1.3.78: 添加多语言支持
 * 选择企业用户后填写企业详细信息
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { completeBusinessOnboarding } from '../services/organizationService';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

interface BusinessSetupPageProps {
  onComplete?: () => void;
}

export function BusinessSetupPage({ onComplete }: BusinessSetupPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    companyName: '',
    companySize: '',
    industry: '',
    jobTitle: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 企业规模选项
  const COMPANY_SIZES = [
    { value: '2-5', label: t('businessSetup.size1to5') },
    { value: '6-20', label: t('businessSetup.size6to20') },
    { value: '21-50', label: t('businessSetup.size21to50') },
    { value: '51+', label: t('businessSetup.size51plus') },
  ];

  // 行业选项
  const INDUSTRIES = [
    { value: 'technology', label: t('businessSetup.industryTech') },
    { value: 'finance', label: t('businessSetup.industryFinance') },
    { value: 'healthcare', label: t('businessSetup.industryHealthcare') },
    { value: 'education', label: t('businessSetup.industryEducation') },
    { value: 'manufacturing', label: t('businessSetup.industryManufacturing') },
    { value: 'retail', label: t('businessSetup.industryRetail') },
    { value: 'consulting', label: t('businessSetup.industryConsulting') },
    { value: 'media', label: t('businessSetup.industryMedia') },
    { value: 'government', label: t('businessSetup.industryGovernment') },
    { value: 'other', label: t('businessSetup.industryOther') },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.companyName.trim()) {
      setError(t('businessSetup.companyNameRequired'));
      return;
    }
    if (!formData.companySize) {
      setError(t('businessSetup.companySizeRequired'));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { result, error: apiError } = await completeBusinessOnboarding(
        formData.companyName.trim(),
        formData.companySize,
        formData.industry || undefined,
        formData.jobTitle || undefined
      );
      
      if (apiError) {
        setError(apiError.message);
        return;
      }
      
      // 完成，跳转到主页
      // 1.3.80: 应用主页改为 /app
      if (onComplete) {
        onComplete();
      } else {
        navigate('/app');
      }
    } catch (err: any) {
      setError(err.message || t('businessSetup.createFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // 1.3.79: 返回到 onboarding 页面
  const handleBack = () => {
    navigate('/onboarding');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
      {/* 返回 Onboarding 按钮 - 左上角 */}
      <button
        onClick={handleBack}
        className="absolute top-4 left-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
        title={t('businessSetup.back')}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      </button>

      {/* 语言切换按钮 - 右上角 */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher direction="down" />
      </div>
      
      <div className="w-full max-w-lg">

        {/* 表单卡片 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            {/* 简约扁平风格图标 */}
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t('businessSetup.title')}</h1>
            <p className="text-gray-500 mt-2">{t('businessSetup.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 企业名称 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('businessSetup.companyName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder={t('businessSetup.companyNamePlaceholder')}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
            </div>

            {/* 企业规模 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('businessSetup.companySize')} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {COMPANY_SIZES.map(size => (
                  <label
                    key={size.value}
                    className={`
                      flex items-center justify-center px-4 py-3 border rounded-xl cursor-pointer transition-all
                      ${formData.companySize === size.value 
                        ? 'border-purple-500 bg-purple-50 text-purple-700' 
                        : 'border-gray-200 hover:border-purple-300'}
                    `}
                  >
                    <input
                      type="radio"
                      name="companySize"
                      value={size.value}
                      checked={formData.companySize === size.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <span className="font-medium">{size.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 行业（选填） */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('businessSetup.industry')} <span className="text-gray-400">（{t('businessSetup.optional')}）</span>
              </label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
              >
                <option value="">{t('businessSetup.industryPlaceholder')}</option>
                {INDUSTRIES.map(industry => (
                  <option key={industry.value} value={industry.value}>
                    {industry.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 职位（选填） */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('businessSetup.jobTitle')} <span className="text-gray-400">（{t('businessSetup.optional')}）</span>
              </label>
              <input
                type="text"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                placeholder={t('businessSetup.jobTitlePlaceholder')}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('businessSetup.creating')}
                </span>
              ) : t('businessSetup.completeRegistration')}
            </button>
          </form>
        </div>

        {/* 底部提示 */}
        <p className="text-center text-gray-500 text-sm mt-6">
          {t('businessSetup.adminNote')}
        </p>
      </div>
    </div>
  );
}
